import * as React from 'react';
import { useCallback } from 'react';
import { css } from '@emotion/core';
import { Switch, Route } from 'react-router-dom';
import { RouteTemplates } from '../../../navigation/routes';
import AddDevPlanItemModal from '../AddDevPlanItemModal';
import EditDevPlanItemModal from '../EditDevPlanItemModal';
import DevPlanItem from '../DevPlanItem';
import DevPlanView from '../DevPlanView';
import { ActionType } from '../../../core/redux/actionType';
import { Dispatch } from 'redux';
import { getRemindersOnAction } from '../reminders/getRemindersOnAction';
import { getDevPlanItemsAction } from '../api/getDevPlanItemsAction';
import { getDevPlanActionsAction } from './api/getDevPlanActionsAction';
import { useMount } from '../../../core/hooks/useMount';
import { useDispatch } from 'react-redux';
import { useAppState } from '../../../core/redux/hooks';
import { PAGE_BG, PAGE_HEIGHT } from '../../../layout/pageStyle';
import { FLEX_COLUMN, FLEX_CENTERED } from '../../../layout/flexBox';
import { Spinner } from '../../../loading/Spinner';
import { COLORS } from '../../../globalStyle/colors';
import ErrorMsg from '../../../error/ErrorMsg';
import { Str } from '../../../localization/translationKeys';
import { useAnyLoading, useAnyError } from '../../../core/api/hooks';
import { getDevPlanFieldsAndAnswersAction } from './api/getDevPlanFieldsAndAnswersAction';
import { getDevPlanStatusesAction } from './api/getDevPlanStatusesAction';
import { participantRatersAction } from '../../survey/raters/participantRatersAction';
import { useProjIds } from '../../../navigation/hooks';
import EmailDevPlanModal from '../emailPlan/EmailDevPlanModal';
import DevPlanRemindersModal from '../reminders/DevPlanRemindersModal';
import { getDevPlanCollaboratorsAction } from '../collaboration/api/getDevPlanCollaboratorsAction';
import { getDevItemCommentsAction } from '../collaboration/api/getDevItemCommentsAction';
import { useDevPlanCollaborationSetting } from '../collaboration/hooks';
import { getDevActionListItemsAction } from '../listItems/api/getDevActionListItemsAction';


const styles = {
  root:    css`
    
  `,
  spinner: css`
    width: 100px;
    height: 100px;
  `,
};


const API_ACTIONS = [
  ActionType.GetRemindersOnRequest,
  ActionType.GetDevPlanItemsRequest,
  ActionType.GetDevPlanActionsRequest,
  ActionType.ParticipantRatersRequest,
  ActionType.GetDevPlanFieldsAndAnswersRequest,
  ActionType.GetStatusKeysRequest,
  ActionType.GetDevActionListItemsRequest,
  ActionType.GetDevPlanCollaboratorsRequest,
  ActionType.GetDevItemCommentsRequest,
];


const loadPlanInfo = (dispatch: Dispatch) => (
  devPlanProjectId: number,
  projectId: number,
  participantId: number,
  languageId: number,
  hasRaters: boolean,
  collaborationEnabled: boolean,
) => {
  if (!hasRaters) {
    dispatch(participantRatersAction(projectId, participantId));
  }

  dispatch(getDevPlanItemsAction(devPlanProjectId, participantId));
  dispatch(getDevPlanActionsAction(devPlanProjectId, participantId));
  dispatch(getDevPlanFieldsAndAnswersAction(devPlanProjectId, participantId));
  dispatch(getDevPlanStatusesAction());
  dispatch(getRemindersOnAction(projectId, participantId));
  dispatch(getDevActionListItemsAction(devPlanProjectId, languageId));

  if (collaborationEnabled) {
    dispatch(getDevPlanCollaboratorsAction(devPlanProjectId, participantId));
    dispatch(getDevItemCommentsAction(devPlanProjectId, participantId));
  }
};


type Props = {};


/**
 * DevPlanRouter: a component that routes URLs for all the dev plan pages
 *
 * PureComponent: true
 * Redux Connected: false
 */
const DevPlanRouter: React.FC<Props> = () => {
  const dispatch = useDispatch();

  const projIds = useProjIds();

  const collaborationEnabled = useDevPlanCollaborationSetting();

  const devPlanProjectId = useAppState(state => state.projectSummary?.devPlanProjectId || projIds.projId);

  const hasRaters = useAppState(state => !!state.projectRaters?.[`${projIds.projId}`]);

  const langId = useAppState(state => state.locale.lang.languageId);

  const loadPlanDetails = useCallback(
    () => loadPlanInfo(dispatch)(
      devPlanProjectId,
      projIds.projId,
      projIds.participantId,
      langId,
      hasRaters,
      collaborationEnabled,
    ),
    [dispatch, devPlanProjectId, projIds.projId, projIds.participantId, langId, hasRaters, collaborationEnabled],
  );

  useMount(loadPlanDetails);

  const isLoading = useAnyLoading(API_ACTIONS);
  const hasError  = useAnyError(API_ACTIONS);

  if (isLoading || hasError) {
    return (
      <div css={[PAGE_BG, PAGE_HEIGHT, FLEX_COLUMN, FLEX_CENTERED]}>
        {
          isLoading
            ? <Spinner fill={COLORS.GREEN} css={styles.spinner} />
            : <ErrorMsg error={Str.LoadFailed} showRetry={true} onRetry={loadPlanDetails} />
        }
      </div>
    );
  }

  return (
    <>
      <Switch>
        <Route path={RouteTemplates.ViewPlanItem} component={DevPlanItem} />
        <Route path={RouteTemplates.DevPlan} component={DevPlanView} />
      </Switch>
      <Switch>
        <Route path={RouteTemplates.AddPlanItem} component={AddDevPlanItemModal} />
        <Route path={RouteTemplates.EditPlanItem} component={EditDevPlanItemModal} />
        <Route path={RouteTemplates.EmailDevPlanModal} component={EmailDevPlanModal} />
        <Route path={RouteTemplates.DevPlanRemindersModal} component={DevPlanRemindersModal} />
      </Switch>
    </>
  );
};

export default DevPlanRouter;
